import classNames from 'classnames';

import Link from '../../../../app/shared/components/Link';
import DateTime from '../../../ui/DateTime';
import { Menu, MenuButton, MenuContent, MenuItem } from '../../../ui/Menu/Menu';
import Skeleton from '../../../ui/Skeleton/Skeleton';
import SkeletonText from '../../../ui/SkeletonText/SkeletonText';
import css from './PricelistListingItem.css';
import PricelistListingItemState from './PricelistListingItemState';

interface PricelistListingItemAction {
  label: string;
  action: () => void;
}

interface PricelistListingItemProps {
  state: PricelistListingItemState;
  actions?: PricelistListingItemAction[];
  className?: string;
}

export const PricelistListingItem = ({ actions, className, state }: PricelistListingItemProps) => {
  const { data, detailsRouteUrl, locale, translation } = state;

  return (
    <div className={classNames(css.root, className)}>
      <div className={css.header}>
        <h2 className={css.heading}>
          <Link className={css.link} to={detailsRouteUrl}>
            {data.name}
          </Link>
        </h2>
        {actions?.any() && (
          <Menu>
            <MenuButton
              className={css.actions}
              aria-label={translation.actionsLabelFormat.interpolate([['name', data.name]])}
            />
            <MenuContent>
              {actions.map(({ action, label }) => (
                <MenuItem key={label} onAction={action}>
                  {label}
                </MenuItem>
              ))}
            </MenuContent>
          </Menu>
        )}
        {data.description && <p className={css.description}>{data.description}</p>}
      </div>
      <div className={css.metadata}>
        <span className={css.label}>{translation.itemsLabel}</span>
        {data.totalProducts}
      </div>
      <div className={css.dates}>
        <span className={css.metadata}>
          <span className={css.label}>{translation.createdAtLabel}</span>
          <DateTime value={data.createdAt} locale={locale} format="date-only" />
        </span>
        <span className={css.metadata}>
          <span className={css.label}>{translation.modifiedAtLabel}</span>
          <DateTime value={data.modifiedAt} locale={locale} format="date-only" />
        </span>
      </div>
    </div>
  );
};

interface PricelistListingItemSkeletonProps {
  className?: string;
}

/**
 * A card in the shape of `PricelistListingItem`, built from that card's own layout classes so the
 * placeholder and the content it stands in for cannot drift apart.
 */
export const PricelistListingItemSkeleton = ({ className }: PricelistListingItemSkeletonProps) => {
  return (
    <div className={classNames(css.skeleton, className)}>
      <SkeletonText className={css.header} lineCount={2} />
      <Skeleton className={css.count} />
      <div className={css.dates}>
        <span className={css.metadata}>
          <Skeleton className={css.date} />
          <Skeleton className={css.date} />
        </span>
        <span className={css.metadata}>
          <Skeleton className={css.date} />
          <Skeleton className={css.date} />
        </span>
      </div>
    </div>
  );
};
